import React from 'react';
import { graphql, Link } from 'gatsby';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import { Layout } from '@components';
import PostCategory from '@components/postCategory';

const serif = `Georgia, 'Iowan Old Style', 'Times New Roman', serif`;

const StyledPostContainer = styled.main`
  .inner {
    max-width: 760px;
    margin: 0 auto;
  }
`;

const StyledPostHeader = styled.header`
  margin-bottom: 50px;

  .post-title {
    margin: 0;
    color: var(--white);
    font-family: ${serif};
    font-size: clamp(34px, 6vw, 54px);
    font-weight: 600;
    line-height: 1.1;
  }

  .post-desc {
    margin: 18px 0 0;
    color: var(--light-slate);
    font-size: var(--fz-xl);
    line-height: 1.5;
  }

  .post-meta {
    margin: 25px 0 0;
    color: var(--slate);
    font-family: var(--font-mono);
    font-size: var(--fz-xxs);
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }
`;

const StyledPostContent = styled.div`
  margin-bottom: 100px;
  font-size: var(--fz-xl);

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin: 2em 0 0.6em;
    font-family: ${serif};
    font-weight: 600;
  }

  p {
    margin: 1.2em 0;
    color: var(--light-slate);
    line-height: 1.6;
  }

  blockquote {
    margin: 1.5em 0;
    padding-left: 20px;
    border-left: 2px solid var(--green);
    color: var(--light-slate);
    font-style: italic;
  }

  a {
    ${({ theme }) => theme.mixins.inlineLink};
  }

  figure {
    margin: 2em 0;
  }

  figcaption {
    margin-top: 12px;
    color: var(--slate);
    font-size: var(--fz-sm);
    font-style: italic;
    text-align: center;
    line-height: 1.5;
  }

  img {
    max-width: 100%;
    height: auto;
    border-radius: var(--border-radius);
  }

  a.gatsby-resp-image-link::after {
    display: none;
  }

  /* Override the image plugin's inline load animation on route changes. */
  .gatsby-resp-image-image {
    opacity: 1 !important;
    transition: none !important;
  }

  .math-block {
    overflow-x: auto;
    padding: 20px 0;
    color: var(--lightest-slate);
  }

  math {
    min-width: max-content;
  }

  code {
    background-color: var(--lightest-navy);
    color: var(--lightest-slate);
    border-radius: var(--border-radius);
    font-size: var(--fz-sm);
    padding: 0.2em 0.4em;
  }

  pre code {
    background-color: transparent;
    padding: 0;
  }

  pre {
    max-width: 100%;
    overflow-x: auto;
    padding: 20px;
    background-color: var(--light-navy);
    border-radius: var(--border-radius);
  }
`;

const BlogPostTemplate = ({ data, location }) => {
  const { frontmatter, html } = data.markdownRemark;
  const { title, description, date, tags } = frontmatter;

  return (
    <Layout location={location}>
      <Helmet title={title} />

      <StyledPostContainer>
        <div className="inner">
          <span className="breadcrumb">
            <span className="arrow">&larr;</span>
            <Link to="/blog">All posts</Link>
          </span>

          <StyledPostHeader>
            {tags?.[0] && <PostCategory>{tags[0]}</PostCategory>}
            <h1 className="post-title">{title}</h1>
            {description && <p className="post-desc">{description}</p>}
            <p className="post-meta">
              <time>
                {new Date(date).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </time>
              &nbsp;&bull;&nbsp; Felix Gan
            </p>
          </StyledPostHeader>

          <StyledPostContent dangerouslySetInnerHTML={{ __html: html }} />
        </div>
      </StyledPostContainer>
    </Layout>
  );
};

export default BlogPostTemplate;

BlogPostTemplate.propTypes = {
  data: PropTypes.object,
  location: PropTypes.object,
};

export const pageQuery = graphql`
  query($path: String!) {
    markdownRemark(frontmatter: { slug: { eq: $path } }) {
      html
      frontmatter {
        title
        description
        date
        slug
        tags
      }
    }
  }
`;
